import { Component, OnInit, ViewChild } from '@angular/core';
import { DesktopSidebarComponent } from "../../../../shared/components/desktop-sidebar/desktop-sidebar.component";
import { DesktopTopbarComponent } from "../../../../shared/components/desktop-topbar/desktop-topbar.component";
import { MobileTopbarComponent } from "../../../../shared/components/mobile-topbar/mobile-topbar.component";
import { TabButtonComponent } from "../../../../shared/components/tab-button/tab-button.component";
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { CustomDropdownComponent } from "../../../../shared/components/custom-dropdown/custom-dropdown.component";
import { CustomButtonComponent } from "../../../../shared/components/custom-button/custom-button.component";
import { LocationService } from '../../../../core/api/location.service';
import { ToastComponent } from '../../../../shared/components/toast/toast.component';
import { Location } from '../../../../core/interfaces/location';
import { HttpErrorResponse } from '@angular/common/http';
import { ApiErrorService } from '../../../../core/api/api-error.service';
import { SolarService } from '../../../../core/api/solar.service';
import { lastValueFrom } from 'rxjs';
import { SolarPotentialChartComponent } from "../../charts/solar-potential-chart/solar-potential-chart.component";
import { BuildingData, GeoTiff, PvCalculationRequest, PvCalculationResponse } from '../../../../core/interfaces/solar-interfaces';
import { CommonModule } from '@angular/common';
import * as geotiff from 'geotiff';
import * as geokeysToProj4 from 'geotiff-geokeys-to-proj4';
import proj4 from 'proj4';
import { GeoTiffRendererComponent } from "../../components/geo-tiff-renderer/geo-tiff-renderer.component";
import { CustomFooterComponent } from "../../../../shared/components/custom-footer/custom-footer.component";
import { ReturnOnInvestmentChartComponent } from "../../charts/return-on-investment-chart/return-on-investment-chart.component";
import { CalendlyPvAnalyseDialogComponent } from "../../components/calendly-pv-analyse-dialog/calendly-pv-analyse-dialog.component";

@Component({
  selector: 'app-solar-pvroof-page',
  standalone: true,
  imports: [DesktopSidebarComponent, DesktopTopbarComponent, MobileTopbarComponent, TabButtonComponent, TranslocoPipe, CustomDropdownComponent, CustomButtonComponent, ToastComponent, GeoTiffRendererComponent, CustomFooterComponent, ReturnOnInvestmentChartComponent, CommonModule, CalendlyPvAnalyseDialogComponent],
  templateUrl: './solar-pvroof-page.component.html',
  styleUrl: './solar-pvroof-page.component.css'
})
export class SolarPVRoofPageComponent implements OnInit {
  selectedSidebarItemId: number = 3;

  // Location Dropdown Menu
  locationsDropdownOptions: string[] = [];
  selectedLocationDropdown: number | undefined;

  // Toast
  @ViewChild(ToastComponent) toast!: ToastComponent;

  // List of locations
  listOfLocations: Location[] = [];

  // Solar data
  loadingData: boolean = false;
  solarData: BuildingData | null = null;

  // Chart
  @ViewChild(ReturnOnInvestmentChartComponent) returnOnInvestmentChart!: ReturnOnInvestmentChartComponent;

  // Extracted data
  annualSunshine: number | null = null;
  annualProduction: number | null = null;
  pvCapacity: number | null = null;
  yearlySavingPotential: number | null = null;
  roiTimeFrame: number | null = null;
  roofUsage: number | null = null;

  initialInvestment: number | null = null;
  yearlyReturn: number | null = null;
  breakEventTime: number | null = null;
  manyYearsReturn: number | null = null;
  coReduction: number | null = null;
  equalTrees: number | null = null;

  roofArea: number | null = null;

  maxPanels: number | null = null;

  dateOfDataUpdate: string | null = null;

  yearlyEnergyPotential: number | null = null;

  panelLifetimeYears: number | null = null;

  geoTiff: GeoTiff | null = null;

  //
  notAvailable: string = "?";

  // Talk to specialist dialog
  @ViewChild(CalendlyPvAnalyseDialogComponent) talkToSpecialistDialog!: CalendlyPvAnalyseDialogComponent;

  constructor(private locationService: LocationService, private apiError: ApiErrorService, private solarService: SolarService, private translocoService: TranslocoService) { }

  ngOnInit(): void {
    this.translocoService.selectTranslate('shared.not_available')
      .subscribe(translation => {
        this.notAvailable = translation;
      });

    this.loadingData = true;
    this.locationService.getLocations().subscribe({
      next: (response) => {
        if (response) {
          this.listOfLocations = response;
        }
      },
      error: async (error) => {
        var crid = 'UNKNOWN-CRID';

        if (error instanceof HttpErrorResponse) {
          crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
        }
        this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
        this.loadingData = false;
      },
      complete: () => {
        this.populateLocationsDropdown();
        this.loadingData = false;
      }
    });
  }

  populateLocationsDropdown() {
    for (let location of this.listOfLocations) {
      const locationString = `${location.street ?? ''} ${location.number ?? ''}, ${location.postal_code ?? ''} ${location.city ?? ''}`;
      this.locationsDropdownOptions.push(locationString);
    }
    if (this.listOfLocations.length > 0) {
      this.selectedLocationDropdown = 0;
      this.calculateSolarInfoButtonClick();
    }
  }

  updateWithExtractedData() {
    this.annualSunshine = this.solarData?.solarPotential?.maxSunshineHoursPerYear ?? null;

    this.roofArea = this.solarData?.solarPotential?.wholeRoofStats?.areaMeters2 ?? null;

    this.maxPanels = this.solarData?.solarPotential?.maxArrayPanelsCount ?? null;

    this.dateOfDataUpdate = this.solarData?.imageryProcessedDate
      ? `${this.solarData.imageryProcessedDate.day}/${this.solarData.imageryProcessedDate.month}/${this.solarData.imageryProcessedDate.year}`
      : null;

    this.yearlyEnergyPotential = this.solarData?.solarPotential?.solarPanelConfigs
      ? this.solarData.solarPotential.solarPanelConfigs[this.solarData.solarPotential.solarPanelConfigs.length - 1]?.yearlyEnergyDcKwh
      : null;

    this.panelLifetimeYears = this.solarData?.solarPotential?.panelLifetimeYears ?? null;

    this.annualProduction = this.yearlyEnergyPotential ?? null;

    const solarPotential = this.solarData?.solarPotential;
    const panelCapacity = solarPotential?.panelCapacityWatts ?? null;
    this.pvCapacity = this.maxPanels && panelCapacity
      ? (this.maxPanels * panelCapacity) / 1000
      : null;

    this.roofArea = solarPotential?.wholeRoofStats?.areaMeters2 ?? null;

    const usedArea = solarPotential?.maxArrayAreaMeters2 ?? null;

    this.roofUsage = usedArea && this.roofArea
      ? parseFloat(((usedArea / this.roofArea) * 100).toFixed(1))
      : null;
  }

  updateCalculationsWithExtractedData(data: PvCalculationResponse) {
    this.initialInvestment = data.initial_investment ?? null;
    this.yearlySavingPotential = data.yearly_saving_potential ?? null;
    this.roiTimeFrame = data.roi_timeframe ?? null;
    this.manyYearsReturn = data.twenty_five_year_return ?? null;
    this.coReduction = data.co2_reduction ?? null;
    this.equalTrees = data.equivalent_trees_planted ?? null;

    this.yearlyReturn = this.yearlySavingPotential;
    this.breakEventTime = this.roiTimeFrame;

    this.returnOnInvestmentChart.updateChart();
  }

  async calculateSolarInfoButtonClick() {
    if (this.listOfLocations.length === 0) {
      this.toast.showToast('shared.error', 'shared.toast.no_locations', 'error', 4000, true);
      return;
    }

    if (this.selectedLocationDropdown === undefined) {
      this.toast.showToast('shared.error', 'shared.toast.select_location', 'error', 4000, true);
      return;
    }

    const location = this.listOfLocations[this.selectedLocationDropdown];

    if (!location.latitude || !location.longitude) {
      this.toast.showToast('shared.error', 'shared.toast.edit_location_no_lat_long', 'error', 4000, true);
      return;
    }

    this.loadingData = true;
    this.solarData = null;
    this.geoTiff = null;

    var googleSolarApiResponse = undefined;

    try {
      googleSolarApiResponse = await lastValueFrom(this.solarService.getBuildingInsights(location.latitude, location.longitude));

      this.solarData = googleSolarApiResponse;
      this.updateWithExtractedData();

      // Load calculations

      const calculationRequest: PvCalculationRequest = {
        max_panels: this.maxPanels ?? 0,
        panel_capacity_watts: this.solarData?.solarPotential?.panelCapacityWatts ?? 0,
        annual_energy_production: this.yearlyEnergyPotential ?? 0,
        years: this.panelLifetimeYears ?? 25
      };

      const calculationsResponse = await lastValueFrom(this.solarService.getPVCalculations(calculationRequest));
      this.updateCalculationsWithExtractedData(calculationsResponse);

      try {
        this.geoTiff = await this.downloadGeoTiff(location);
      } catch (tiffError) {
        this.geoTiff = null;
      }

    } catch (error) {
      this.solarData = null;
      this.loadingData = false;

      if (error instanceof HttpErrorResponse) {
        if (error.error.code == 404) {
          this.toast.showToast('shared.error', 'shared.toast.solar_data_unavailable', 'error', 4000, true);
          return;
        }
      }
      this.toast.showToast('shared.error', 'shared.toast.solar_error', 'error', 4000, true);
      return;
    }
    this.loadingData = false;
  }

  async downloadGeoTiff(location: Location): Promise<GeoTiff> {
    try {
      let dataLayers = await lastValueFrom(this.solarService.getBuildingDataLayers(location.latitude!, location.longitude!));

      let response = await lastValueFrom(this.solarService.getTiffImage(dataLayers.rgbUrl));

      // Get the GeoTIFF rasters, which are the pixel values for each band.
      const arrayBuffer = await response.arrayBuffer();
      const tiff = await geotiff.fromArrayBuffer(arrayBuffer);
      const image = await tiff.getImage();
      const rasters = await image.readRasters();

      // Reproject the bounding box into lat/lon coordinates.
      const geoKeys = image.getGeoKeys();
      const projObj = geokeysToProj4.toProj4(geoKeys);
      const projection = proj4(projObj.proj4, 'WGS84');
      const box = image.getBoundingBox();
      const sw = projection.forward({
        x: box[0] * projObj.coordinatesConversionParameters.x,
        y: box[1] * projObj.coordinatesConversionParameters.y,
      });
      const ne = projection.forward({
        x: box[2] * projObj.coordinatesConversionParameters.x,
        y: box[3] * projObj.coordinatesConversionParameters.y,
      });

      return {

        width: rasters.width,
        height: rasters.height,
        rasters: [...Array(rasters.length).keys()].map((i) =>
          Array.from(rasters[i] as geotiff.TypedArray),
        ),
        // The bounding box as a lat/lon rectangle.
        bounds: {
          north: ne.y,
          south: sw.y,
          east: ne.x,
          west: sw.x,
        },
      };
    } catch (error) {
      throw error;
    }
  }

  async onDropdownOptionChange(event: any) {
    if (this.listOfLocations.length === 0) {
      this.toast.showToast('shared.error', 'shared.toast.no_locations', 'error', 4000, true);
      return;
    }

    if (this.selectedLocationDropdown === undefined) {
      this.toast.showToast('shared.error', 'shared.toast.select_location', 'error', 4000, true);
      return;
    }

    const location = this.listOfLocations[this.selectedLocationDropdown];

    if (!location.latitude || !location.longitude) {
      this.toast.showToast('shared.error', 'shared.toast.edit_location_no_lat_long', 'error', 4000, true);
      return;
    }

    if (location.is_active == false) {
      this.toast.showToast('shared.error', 'shared.toast.location_inactive_error', 'error', 4000, true);
      return;
    }

    this.loadingData = true;
    this.solarData = null;
    this.geoTiff = null;

    var googleSolarApiResponse = undefined;

    try {
      googleSolarApiResponse = await lastValueFrom(this.solarService.getBuildingInsights(location.latitude, location.longitude));

      this.solarData = googleSolarApiResponse;
      this.updateWithExtractedData();

      // Load calculations

      const calculationRequest: PvCalculationRequest = {
        max_panels: this.maxPanels ?? 0,
        panel_capacity_watts: this.solarData?.solarPotential?.panelCapacityWatts ?? 0,
        annual_energy_production: this.yearlyEnergyPotential ?? 0,
        years: this.panelLifetimeYears ?? 25
      };

      const calculationsResponse = await lastValueFrom(this.solarService.getPVCalculations(calculationRequest));
      this.updateCalculationsWithExtractedData(calculationsResponse);

      try {
        this.geoTiff = await this.downloadGeoTiff(location);
      } catch (tiffError) {
        this.geoTiff = null;
      }

    } catch (error) {
      this.solarData = null;
      this.loadingData = false;

      if (error instanceof HttpErrorResponse) {
        if (error.error.code == 404) {
          this.toast.showToast('shared.error', 'shared.toast.solar_data_unavailable', 'error', 4000, true);
          return;
        }
      }
      this.toast.showToast('shared.error', 'shared.toast.solar_error', 'error', 4000, true);
      return;
    }
    this.loadingData = false;
  }

  talkToSpecialist() {
    this.talkToSpecialistDialog.openModal();
  }
}
